import { type FC } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import {
  type ScratchOfferPrize,
  type ScratchRewardType,
} from '@/common/types/scratch';
import {
  H2,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { CurrencyImage } from '@/components/ui/CurrencyImage/CurrencyImage';

import css from './styles.module.scss';

const STATIC_IMG: Partial<Record<ScratchRewardType, string>> = {
  goldTicket: '/img/icons/ticket-gold.webp',
  silverTicket: '/img/icons/ticket-silver.webp',
  diamondTicket: '/img/icons/ticket-diamond.webp',
  starPoints: '/img/icons/star-large.png',
  bingoBalls: '/img/bingo/bingo-icon.png',
};

const CURRENCY_MAP: Partial<
  Record<ScratchRewardType, 'tgem' | 'cash' | 'dice' | 'star'>
> = {
  tgem: 'tgem',
  cash: 'cash',
  dice: 'dice',
};

interface ScratchRewardPickerProps {
  open: boolean;
  selectedKey: string | null;
  /** Inline list of rewards this offer exposes — picker shows exactly these. */
  rewardPool: ScratchOfferPrize[];
  onClose: () => void;
  onPick: (key: string) => void;
}

/**
 * Bottom-sheet that lists every possible reward and lets the player commit
 * to one as their target. The selected reward becomes the win condition
 * the next time the player scratches.
 */
const ScratchRewardPicker: FC<ScratchRewardPickerProps> = ({
  open,
  selectedKey,
  rewardPool,
  onClose,
  onPick,
}) => {
  const { t } = useTranslation();

  const visiblePrizes = rewardPool;

  // Portal to body so the sheet's `position: fixed` is anchored to the
  // viewport and not to any ancestor with a `transform` (framer-motion's
  // animated containers create new containing blocks that break fixed).
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className={css.backdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          <motion.div
            className={css.sheet}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
          >
            <div className={css.handle} />

            <div className={css.header}>
              <H2 weight="bold" className={css.title}>
                {t('Choose your reward')}
              </H2>
              <StringXSmall className={css.subtitle}>
                {t('Match all 3 cells to win it')}
              </StringXSmall>
            </div>

            <div className={css.list}>
              {visiblePrizes.map((p) => {
                const key = `${p.rewardType}-${p.value}`;
                const currencyKind = CURRENCY_MAP[p.rewardType];
                const imgSrc = !currencyKind
                  ? (STATIC_IMG[p.rewardType] ?? null)
                  : null;
                const isSelected = key === selectedKey;

                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => onPick(key)}
                    className={cn(
                      css.item,
                      css[`rarity_${p.rarity}`],
                      isSelected && css.itemSelected,
                    )}
                  >
                    <div className={css.iconBox}>
                      {currencyKind ? (
                        <CurrencyImage kind={currencyKind} size="36px" />
                      ) : imgSrc ? (
                        <img src={imgSrc} alt="" className={css.iconImg} />
                      ) : null}
                    </div>
                    <div className={css.text}>
                      <StringMain weight="bold" className={css.value}>
                        ×{p.value.toLocaleString()}
                      </StringMain>
                      <StringXSmall className={css.rarityText}>
                        {t(p.rarity[0].toUpperCase() + p.rarity.slice(1))}
                      </StringXSmall>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export { ScratchRewardPicker };
